import { WorkloadView } from '@components/workload/filters/types';

export enum ESupportedLangs {
  en = 'en',
  es = 'es',
}

export type JobStageStatus =
  | 'toSchedule'
  | 'scheduleRequestSent'
  | 'scheduled'
  | 'inProgress'
  | 'completed'
  | 'warning'
  | 'error';

export const supportedLanguages: string[] = Object.values(ESupportedLangs);

export const getLangFromPath = () => {
  const p = window.location.pathname;
  const isSupportedLang = supportedLanguages.find((lang) => p.startsWith(`/${lang}`));
  if (isSupportedLang) return isSupportedLang;
  return 'en';
};

export const jobIdRoute = {
  index: '',
  materials: 'materials',
  inventory: 'inventory',
  documents: 'documents',
  questionnaires: 'questionnaires',
  questionnaire: 'questionnaires/:questionaryId',
  workProgress: 'work-progress',
  workProgressStage: ':stageId',
};

export const questionnairesRoute = {
  index: 'questionnaires',
  questionnaire: (id?: string) => id || ':questionaryId',
};

export const foremanRoute = {
  index: 'field-user',
  archive: 'archive',
  schedules: 'schedules',
  questionnaires: 'questionnaires',
  questionnaire: (questionaryId: string | undefined) =>
    [questionnairesRoute.index, questionnairesRoute.questionnaire(questionaryId)].join('/'),
  form: (pointId: string) => `form/${pointId}`,
  other: 'other',
  taskId: ':taskId',
  routeId: ':routeId',
  pointId: ':pointId',
  questionId: ':questionId',
  taskQuestionnaire: 'questionnaires/:questionaryId',
  questionnaireQuestion: 'questions/:questionId',
  questions: (questionId: string | undefined, params: string) =>
    [['questions', questionId].join('/'), params].join(''),
};

export const resourcesRoute = {
  index: 'resources',
  office: 'office',
  fieldWorkers: 'fieldworkers',
  equipment: 'equipment',
  acceptInvitation: 'accept-invitation',
};

export const materialsRoute = {
  index: 'materials',
  templates: 'templates',
  tags: 'tags',
};

export const panelRoute = {
  index: 'panel',
};

export const adminsRoute = {
  index: 'admins',
  acceptInvitation: 'accept-invitation',
};

export const tenantsRoute = {
  index: 'tenants',
  tenant: (id?: string) => id || ':tenantId',
  settings: 'settings',
  dashboards: 'dashboards',
  specialties: 'specialties',
  divisions: 'divisions',
  globalItems: 'global-items',
};

export const workflowsRoute = {
  index: 'workflows',
  workflow: (id?: string) => id || ':workflowId',
  materials: 'materials',
  questionnaire: (id?: string) => ['questionnaires', id || ':questionnaireId'].join('/'),
  workProgress: 'work-progress',
};

export const buildersRoute = {
  index: 'builders',
  builder: (id?: string) => id || ':builderId',
};

export const appRoutes = {
  index: '/',
  base: '',
  back: '..',
  id: ':id',
  notFound: 'not-found',

  resources: {
    index: resourcesRoute.index,
    office: [resourcesRoute.index, resourcesRoute.office].join('/'),
    fieldWorkers: [resourcesRoute.index, resourcesRoute.fieldWorkers].join('/'),
    equipment: [resourcesRoute.index, resourcesRoute.equipment].join('/'),
    acceptInvitation: [resourcesRoute.index, resourcesRoute.acceptInvitation].join('/'),
  },

  tenantResources: {
    office: (tenantId?: string) =>
      [
        panelRoute.index,
        tenantsRoute.index,
        tenantsRoute.tenant(tenantId),
        resourcesRoute.index,
        resourcesRoute.office,
      ].join('/'),
    fieldWorkers: (tenantId?: string) =>
      [
        panelRoute.index,
        tenantsRoute.index,
        tenantsRoute.tenant(tenantId),
        resourcesRoute.index,
        resourcesRoute.fieldWorkers,
      ].join('/'),
    equipment: (tenantId?: string) =>
      [
        panelRoute.index,
        tenantsRoute.index,
        tenantsRoute.tenant(tenantId),
        resourcesRoute.index,
        resourcesRoute.equipment,
      ].join('/'),
  },

  builders: buildersRoute.index,
  builder: (id?: string) => [buildersRoute.index, buildersRoute.builder(id)].join('/'),

  panel: panelRoute.index,

  admins: [panelRoute.index, adminsRoute.index].join('/'),

  workflows: [panelRoute.index, workflowsRoute.index].join('/'),
  workflow: (workflowId?: string) =>
    [panelRoute.index, workflowsRoute.index, workflowsRoute.workflow(workflowId)].join('/'),
  workflowMaterials: (workflowId?: string) =>
    [
      panelRoute.index,
      workflowsRoute.index,
      workflowsRoute.workflow(workflowId),
      workflowsRoute.materials,
    ].join('/'),
  workflowQuestionnaire: (workflowId?: string, questionnaireId?: string) =>
    [
      panelRoute.index,
      workflowsRoute.index,
      workflowsRoute.workflow(workflowId),
      workflowsRoute.questionnaire(questionnaireId),
    ].join('/'),
  workflowWorkProgress: (workflowId?: string) =>
    [
      panelRoute.index,
      workflowsRoute.index,
      workflowsRoute.workflow(workflowId),
      workflowsRoute.workProgress,
    ].join('/'),

  tenants: [panelRoute.index, tenantsRoute.index].join('/'),
  tenant: (id?: string) =>
    [panelRoute.index, tenantsRoute.index, tenantsRoute.tenant(id)].join('/'),
  tenantResourcesOffice: (tenantId?: string) =>
    [
      panelRoute.index,
      tenantsRoute.index,
      tenantsRoute.tenant(tenantId),
      resourcesRoute.index,
      resourcesRoute.office,
    ].join('/'),
  tenantResourcesFieldWorkers: (tenantId?: string) =>
    [
      panelRoute.index,
      tenantsRoute.index,
      tenantsRoute.tenant(tenantId),
      resourcesRoute.index,
      resourcesRoute.fieldWorkers,
    ].join('/'),
  tenantResourcesEquipment: (tenantId?: string) =>
    [
      panelRoute.index,
      tenantsRoute.index,
      tenantsRoute.tenant(tenantId),
      resourcesRoute.index,
      resourcesRoute.equipment,
    ].join('/'),
  tenantWorkflows: (tenantId?: string, jobId?: string) =>
    [
      jobId ? `jobs/${jobId}` : panelRoute.index,
      tenantsRoute.index,
      tenantsRoute.tenant(tenantId),
      workflowsRoute.index,
    ].join('/'),
  tenantWorkflow: (tenantId?: string, workflowId?: string, jobId?: string) =>
    [
      jobId ? `jobs/${jobId}` : panelRoute.index,
      tenantsRoute.index,
      tenantsRoute.tenant(tenantId),
      workflowsRoute.index,
      workflowsRoute.workflow(workflowId),
    ].join('/'),
  tenantWorkflowMaterials: (
    tenantId?: string,
    workflowId?: string,
    projectId?: string,
    jobId?: string,
  ) =>
    [
      projectId ? `projects/${projectId}` : '',
      jobId ? `jobs/${jobId}` : panelRoute.index,
      tenantsRoute.index,
      tenantsRoute.tenant(tenantId),
      workflowsRoute.index,
      workflowsRoute.workflow(workflowId),
      workflowsRoute.materials,
    ]
      .filter(Boolean)
      .join('/'),
  tenantWorkflowQuestionnaire: (
    tenantId?: string,
    workflowId?: string,
    questionnaireId?: string,
    projectId?: string,
    jobId?: string,
  ) =>
    [
      projectId ? `projects/${projectId}` : '',
      jobId ? `jobs/${jobId}` : panelRoute.index,
      tenantsRoute.index,
      tenantsRoute.tenant(tenantId),
      workflowsRoute.index,
      workflowsRoute.workflow(workflowId),
      workflowsRoute.questionnaire(questionnaireId),
    ]
      .filter(Boolean)
      .join('/'),
  tenantWorkflowWorkProgress: (
    tenantId?: string,
    workflowId?: string,
    projectId?: string,
    jobId?: string,
  ) =>
    [
      projectId ? `projects/${projectId}` : '',
      jobId ? `jobs/${jobId}` : panelRoute.index,
      tenantsRoute.index,
      tenantsRoute.tenant(tenantId),
      workflowsRoute.index,
      workflowsRoute.workflow(workflowId),
      workflowsRoute.workProgress,
    ]
      .filter(Boolean)
      .join('/'),
  tenantMaterialsTemplates: (tenantId?: string) =>
    [
      panelRoute.index,
      tenantsRoute.index,
      tenantsRoute.tenant(tenantId),
      materialsRoute.index,
      materialsRoute.templates,
    ].join('/'),
  tenantMaterialsTags: (tenantId?: string) =>
    [
      panelRoute.index,
      tenantsRoute.index,
      tenantsRoute.tenant(tenantId),
      materialsRoute.index,
      materialsRoute.tags,
    ].join('/'),
  tenantSettings: (tenantId?: string) =>
    [
      panelRoute.index,
      tenantsRoute.index,
      tenantsRoute.tenant(tenantId),
      tenantsRoute.settings,
    ].join('/'),
  tenantDashboards: (tenantId?: string) =>
    [
      panelRoute.index,
      tenantsRoute.index,
      tenantsRoute.tenant(tenantId),
      tenantsRoute.dashboards,
    ].join('/'),
  tenantSpecialties: (tenantId?: string) =>
    [
      panelRoute.index,
      tenantsRoute.index,
      tenantsRoute.tenant(tenantId),
      tenantsRoute.specialties,
    ].join('/'),
  tenantDivisions: (tenantId?: string) =>
    [
      panelRoute.index,
      tenantsRoute.index,
      tenantsRoute.tenant(tenantId),
      tenantsRoute.divisions,
    ].join('/'),
  tenantGlobalItems: (tenantId?: string) =>
    [
      panelRoute.index,
      tenantsRoute.index,
      tenantsRoute.tenant(tenantId),
      tenantsRoute.globalItems,
    ].join('/'),
  form: (id: string) => `form/${id}`,
  foremanArchive: `${foremanRoute.index}/${foremanRoute.archive}`,
  foremanSchedules: `${foremanRoute.index}/${foremanRoute.schedules}`,
  foremanOther: `${foremanRoute.index}/${foremanRoute.other}`,
  passwordRecovery: 'password-recovery',
  passwordRecoveryInfo: 'password-recovery/info',
  passwordReset: 'password-reset',
  passwordResetConfirmation: 'password-reset/confirmed',
  jobs: 'jobs',
  jobPreview: (id: string) => `preview/${id}`,
  job: (projectId: string, jobId: string) => `projects/${projectId}/jobs/${jobId}`,
  jobMaterials: (projectId: string, jobId: string) =>
    `projects/${projectId}/jobs/${jobId}/${jobIdRoute.materials}`,
  jobInventory: (projectId: string, jobId: string) =>
    `projects/${projectId}/jobs/${jobId}/${jobIdRoute.inventory}`,
  jobDocuments: (projectId: string, jobId: string) =>
    `projects/${projectId}/jobs/${jobId}/${jobIdRoute.documents}`,
  jobQuestionnaires: (projectId: string, jobId: string) =>
    `projects/${projectId}/jobs/${jobId}/${questionnairesRoute.index}`,
  jobQuestionnaire: (projectId: string, jobId: string, questionaryId: string) =>
    [
      appRoutes.job(projectId, jobId),
      questionnairesRoute.index,
      questionnairesRoute.questionnaire(questionaryId),
    ].join('/'),
  jobWorkProgress: (projectId: string, jobId: string) =>
    `projects/${projectId}/jobs/${jobId}/${jobIdRoute.workProgress}`,
  jobWorkflow: (projectId?: string, id?: string, tenantId?: string, workflowId?: string) =>
    [
      'projects',
      projectId,
      'jobs',
      id,
      tenantsRoute.index,
      tenantsRoute.tenant(tenantId),
      workflowsRoute.index,
      workflowsRoute.workflow(workflowId),
      workflowsRoute.workProgress,
    ].join('/'),
  hotSeat: 'hot-seat',
  onDeck: 'on-deck',
  workload: 'workload',
  companies: 'companies',
  company: (id: string) => `companies/${id}`,
  inventory: 'inventory',
  map: 'map',
  dashboards: 'dashboards',
};

export const queryKeys = {
  dropdowns: {
    additive: ['additive-dropdown'],
    builders: ['builders-dropdown'],
    builderSuperintendents: ['builder-superintendents-dropdown'],
    companies: ['companies-dropdown'],
    concreteMix: ['concrete-mix-dropdown'],
    divisions: ['divisions-dropdown'],
    filtersPresets: ['filters-presets-dropdown'],
    inventoryItems: ['inventory-items-dropdown'],
    locations: ['locations-dropdown'],
    panel: {
      roles: ['roles-dropdown'],
      specialties: ['specialties-dropdown'],
      statuses: ['statuses-dropdown'],
      tenants: ['tenants-dropdown'],
      users: ['users-dropdown'],
      zones: ['zones-dropdown'],
      equipment: ['equipment-dropdown'],
      materialsTags: ['materials-tags-dropdown'],
      globalItemsWeightGroups: ['global-items-weight-groups-dropdown'],
    },
    pourElements: ['pour-elements-dropdown'],
    pourMethods: ['pour-methods-dropdown'],
    routes: ['routes-dropdown'],
    sites: ['sites-dropdown'],
    specialties: ['specialties-dropdown'],
    stages: ['stages-dropdown'],
    subtasksForFiles: ['subtasks-for-files-dropdown'],
    tasks: ['tasks-dropdown'],
    taskTypes: ['task-types-dropdown'],
    users: {
      index: ['users-dropdown'],
    },
    workflows: ['workflows-dropdown'],
    tenantRoles: ['tenant-roles-dropdown'],
    equipment: ['equipment-dropdown'],
  },

  builders: ['builders'],
  builder: ['builder'],

  resources: {
    users: ['resources-users'],
    equipment: ['resources-equipment'],
  },

  users: ['users'],
  user: ['user'],

  admins: ['admins'],
  admin: ['admin'],

  tenants: ['tenants'],
  tenant: ['tenant'],
  tenantIntegration: ['tenant-integration'],

  workflows: {
    index: ['workflows'],
    tasks: {
      schedulable: ['workflows-tasks-schedulable'],
    },
    item: ['workflow'],
    compare: ['workflow-compare'],
    questionnaires: {
      index: ['workflow-questionnaires'],
      item: ['workflow-questionnaire'],
    },
    dropdowns: {
      conditions: {
        records: {
          related: ['workflow-conditions-related-records'],
          referable: ['workflow-conditions-referable-records'],
        },
        statuses: ['workflow-conditions-statuses'],
        types: ['workflow-conditions-types'],
        users: ['workflow-conditions-users'],
      },
      task: {
        materials: {
          records: ['workflow-tasks-materials-records'],
        },
        confirmations: {
          index: ['workflows-task-confirmations'],
          date: ['workflows-task-date-confirmations'],
        },
        tasks: {
          dependent: ['workflows-task-dependent-tasks'],
          readyToSchedule: ['workflows-task-ready-to-schedule-tasks'],
        },
        types: {
          index: ['workflows-task-types'],
          user: ['workflows-task-user-types'],
          schedule: ['workflows-task-schedule-types'],
        },
      },
      subtask: {
        confirmations: {
          task: ['workflows-subtask-task-confirmations'],
        },
        tasks: { allocation: ['workflows-subtask-allocation-tasks'] },
        materials: ['workflows-subtask-materials'],
        confirmationDependentSubtasks: ['workflows-subtask-confirmation-dependent-subtasks'],
        questionnaires: ['workflows-subtask-questionnaires'],
        types: {
          allocation: ['workflows-subtask-allocation-types'],
          allowedFile: ['workflows-subtask-allowed-file-types'],
        },
      },
    },
  },

  dashboards: {
    index: ['dashboards'],
    columns: ['dashboards-columns'],
    filters: ['dashboards-filters'],
  },

  questionnaires: {
    index: ['questionnaires'],
    item: ['questionnaire'],
    sections: ['questionnaires-sections'],
    questions: ['questionnaires-sections-questions'],
    answers: ['questionnaires-sections-questions-answers'],
  },

  specialties: {
    index: ['specialties'],
  },

  divisions: {
    index: ['divisions'],
  },

  globalItems: {
    index: ['global-items'],
    groups: ['global-items-groups'],
  },

  customSettings: {
    index: ['custom-settings'],
  },

  tenantMaterialsTags: {
    index: ['tenant-materials-tags'],
  },

  tenantMaterialsTemplates: {
    index: ['tenant-materials-templates'],
    item: ['tenant-materials-template'],
    compare: ['tenant-materials-template-compare'],
  },

  schedules: {
    internal: {
      index: ['schedules-internal'],
      multischedules: ['schedules-internal-multischedules'],
      item: ['schedules-internal-item'],
    },
    conflicts: ['schedules-conflicts'],
    equipmentConflicts: ['equipment-schedules-conflicts'],
  },

  foremanSchedules: ['foreman-schedules'],
  foremanRoute: ['foreman-route'],
  foremanArchive: ['foreman-archive'],
  foremanTask: ['foreman-task'],
  foremanStages: ['foreman-stages'],
  foremanOther: ['foreman-other'],
  jobProject: ['job-project'],
  job: ['job'],
  jobs: ['jobs'],
  jobStages: ['job-stages'],
  jobStagesMenu: ['job-stages-menu'],
  jobInventory: ['job-inventory'],
  jobInventoryDialog: ['job-inventory-dialog'],
  jobConcreteDirect: ['job-concrete-direct'],
  jobPreview: ['job-preview'],
  hotSeat: ['hot-seat'],
  superintendent: ['superintendent'],
  notifications: ['notifications'],
  webpushNotifications: ['webpushNotifications'],
  documents: ['documents'],
  foremanDocuments: ['foreman-documents'],
  materials: ['materials'],
  foremanMaterials: ['foreman-materials'],
  workloadDaily: ['workload-daily'],
  workloadWeeklyCalendar: ['workload-weekly-calendar'],
  workloadTwoWeeklyCalendar: ['workload-two-weekly-calendar'],
  workloadMonthlyCalendar: ['workload-monthly-calendar'],
  workloadCalendarSidebar: ['workload-calendar-sidebar'],
  workload: ['workload'],
  workloadSchedulesToPublish: ['workload-schedules-to-publish'],
  filters: ['filters'],
  companies: ['companies'],
  company: ['company'],
  contacts: ['contacts'],
  files: ['files'],
  inventoryRoutes: ['inventory-routes'],
  inventoryRequested: ['inventory-requested'],
  inventoryRequestedLocation: ['inventory-requested-location'],
  inventoryAvailable: ['inventory-available'],
  inventoryAvailableLocation: ['inventory-available-location'],
  inventoryCustomAdd: ['inventory-custom-add'],
  inventoryMissingLoad: ['inventory-missing-load'],
  inventoryRemainingLoad: ['inventory-remaining-load'],
  locationItems: ['location-items'],
  map: ['map'],
  mapMenu: ['map-menu'],
  questionnaire: ['questionnaire'],
  questionnaireAttachment: ['questionnaire-attachment'],
};

export const mutationKeys = {
  fileUpload: ['file-upload'],
};

export const passwordRegEx = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[=.,}#?!@$%^&*-]).{8,}$/;

export const uuidRegEx =
  /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/;

export const geolocalizationRegEx =
  /^[-]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/;

export const slugRegEx = /^[a-z0-9]+(?:[_-][a-z0-9]+)*$/;

export const errorKeyRegEx = /^\w+$/;

export const isSecureConnection = window.location.protocol.includes('https');

export const foremanWidth = 160;

export const dailyViewForemanWidth = 300;

export const workloadViews: WorkloadView[] = ['inventory'];

export const use24h = import.meta.env.VITE_TIME_FORMAT === '24h';
